import { type GameDefinition } from '@kite/engine';
import { element } from './dom';

/** Called when a tile is chosen. */
export type GameChosenListener = (gameId: string) => void;

/**
 * The game picker: one big tile per registered game.
 *
 * HTML rather than in-canvas entities. The portal's chrome is a web page and gets the browser's own
 * focus handling, scrolling and accessibility for nothing; in-canvas UI is for what happens inside a
 * game, where it has to sit in world space beside the art.
 *
 * The screen knows only the {@link GameDefinition} list it was handed, so adding a game is still one
 * line in the registry and no change here.
 */
export class HomeScreen {
  /** Root element, appended by the shell. */
  readonly element: HTMLElement;

  constructor(games: readonly GameDefinition[], onChoose: GameChosenListener) {
    this.element = element('main', 'portal-home');

    const title = element('h1', 'portal-home__title', 'Play');

    const grid = element('div', 'portal-home__grid');
    for (const game of games) {
      grid.appendChild(createTile(game, onChoose));
    }

    this.element.append(title, grid);
  }

  /** Whether the picker is on screen. */
  get visible(): boolean {
    return !this.element.hidden;
  }

  set visible(value: boolean) {
    this.element.hidden = !value;
  }

  /** Removes the screen from the page. */
  dispose(): void {
    this.element.remove();
  }
}

function createTile(game: GameDefinition, onChoose: GameChosenListener): HTMLButtonElement {
  const tile = element('button', 'portal-tile');
  tile.type = 'button';

  const thumbnail = element('img', 'portal-tile__image');
  thumbnail.src = game.thumbnail;
  // The label below carries the name, so the image itself is decoration to a screen reader.
  thumbnail.alt = '';
  thumbnail.draggable = false;

  // No class: the tile is a flex column and hands the label its font, so a hook here would style nothing.
  const label = document.createElement('span');
  label.textContent = game.title;

  tile.append(thumbnail, label);
  tile.addEventListener('click', () => {
    onChoose(game.id);
  });
  return tile;
}
